import React from 'react'
import { Button, ColorPicker, Image, Space, Typography } from 'antd'
import type { Color } from 'antd/es/color-picker'
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { ImageUploadButton } from '../BenefitConfig/shared'

const { Text } = Typography

/**
 * liuwei_b1（2026-10-14）：小程序首页横幅（mp_banner）多张配置控件。
 *
 * Contract Pack（planning/liuwei-s0-b1-plan.md，字段名逐字，不得改名）：
 * - MpBanner{imageUrl, imageUrlEn, bgColor}；MP_BANNER_MAX = 5
 * - imageUrl 必填，须以 /uploads/ 或 https:// 开头；imageUrlEn 可空（非空同规则）
 * - bgColor 可空；非空须为 #RRGGBB
 * - 存储：config key mp_banner = JSON 数组字符串；旧值为单个 URL 字符串（兼容读取）
 * - 保存：PATCH /api/admin/configs body { mp_banner: MpBanner[] }
 */
export interface MpBanner {
  imageUrl: string
  imageUrlEn: string | null
  bgColor: string | null
}

export const MP_BANNER_MAX = 5

const IMAGE_URL_RE = /^(\/uploads\/|https:\/\/)/
const BG_COLOR_RE = /^#[0-9A-Fa-f]{6}$/

const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null)

/**
 * 读取已存 mp_banner 原值：
 * - 空 / 未设置 → []
 * - JSON 数组字符串 → MpBanner[]（逐项规整，非对象项丢弃）
 * - 其它非空字符串（旧版单个 URL）→ [{ imageUrl: s, imageUrlEn: null, bgColor: null }]
 * - 以 [ 开头但 JSON 解析失败 → [] + console.error（与后端「坏 JSON → []」口径一致）
 */
export const parseMpBanner = (raw: string | null | undefined): MpBanner[] => {
  const s = (raw ?? '').trim()
  if (!s) return []
  if (!s.startsWith('[')) return [{ imageUrl: s, imageUrlEn: null, bgColor: null }]
  try {
    const arr: unknown = JSON.parse(s)
    if (!Array.isArray(arr)) return []
    return arr
      .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
      .map((x) => ({
        imageUrl: typeof x.imageUrl === 'string' ? x.imageUrl : '',
        imageUrlEn: strOrNull(x.imageUrlEn),
        bgColor: strOrNull(x.bgColor),
      }))
  } catch (err) {
    console.error('[mp_banner] 配置值 JSON 解析失败，按空列表处理', err)
    return []
  }
}

/** Form.Item validator：任一行缺中文图 / URL 或颜色格式不符时阻止保存，提示具体第几张 */
export const validateMpBanners = async (_: unknown, value?: MpBanner[]): Promise<void> => {
  const list = value ?? []
  if (list.length > MP_BANNER_MAX) throw new Error(`首页横幅最多 ${MP_BANNER_MAX} 张`)
  for (let i = 0; i < list.length; i += 1) {
    const b = list[i]
    const n = i + 1
    if (!b.imageUrl) throw new Error(`第 ${n} 张横幅缺少图片，请上传或删除该行`)
    if (!IMAGE_URL_RE.test(b.imageUrl)) throw new Error(`第 ${n} 张横幅图片地址无效（须为上传图片或 https 地址），请重新上传`)
    if (b.imageUrlEn && !IMAGE_URL_RE.test(b.imageUrlEn)) throw new Error(`第 ${n} 张横幅英文图片地址无效，请重新上传或清除`)
    if (b.bgColor && !BG_COLOR_RE.test(b.bgColor)) throw new Error(`第 ${n} 张横幅背景色格式无效，请重新选择或清除`)
  }
}

const BTN_STYLE: React.CSSProperties = { minHeight: 44, minWidth: 44 }

interface RowProps {
  banner: MpBanner
  index: number
  total: number
  disabled?: boolean
  onPatch: (patch: Partial<MpBanner>) => void
  onMove: (delta: -1 | 1) => void
  onRemove: () => void
}

const BannerRow: React.FC<RowProps> = ({ banner, index, total, disabled, onPatch, onMove, onRemove }) => {
  const onColorChange = (c: Color) => {
    const cleared = (c as unknown as { cleared?: boolean }).cleared
    onPatch({ bgColor: cleared ? null : c.toHexString().slice(0, 7).toUpperCase() })
  }
  return (
    <div style={{ display: 'flex', gap: 12, padding: 12, background: '#fafafa', borderRadius: 8, flexWrap: 'wrap' }}>
      <div
        style={{
          width: 120,
          height: 68,
          borderRadius: 6,
          overflow: 'hidden',
          background: banner.bgColor ?? '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {banner.imageUrl ? (
          <Image src={banner.imageUrl} width={120} height={68} style={{ objectFit: 'cover' }} />
        ) : (
          <Text type="danger" style={{ fontSize: 12 }}>未上传</Text>
        )}
      </div>
      <Space direction="vertical" size={6} style={{ flex: 1, minWidth: 200 }}>
        <Text strong>第 {index + 1} 张</Text>
        <Space wrap size={8}>
          <ImageUploadButton
            disabled={disabled}
            text={banner.imageUrl ? '更换中文图' : '上传中文图（必填）'}
            onUploaded={(url) => onPatch({ imageUrl: url })}
          />
          <ImageUploadButton
            disabled={disabled}
            text={banner.imageUrlEn ? '更换英文图' : '上传英文图（选填）'}
            onUploaded={(url) => onPatch({ imageUrlEn: url })}
          />
          {banner.imageUrlEn && (
            <Button size="small" type="link" danger disabled={disabled} onClick={() => onPatch({ imageUrlEn: null })}>
              清除英文图
            </Button>
          )}
        </Space>
        {banner.imageUrlEn && (
          <Image src={banner.imageUrlEn} width={80} height={45} style={{ objectFit: 'cover', borderRadius: 4 }} />
        )}
        <Space size={8} align="center">
          <Text type="secondary" style={{ fontSize: 12 }}>背景色（选填）</Text>
          <ColorPicker
            value={banner.bgColor ?? undefined}
            allowClear
            disabledAlpha
            format="hex"
            disabled={disabled}
            onChange={onColorChange}
            onClear={() => onPatch({ bgColor: null })}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>{banner.bgColor ?? '未设置'}</Text>
        </Space>
      </Space>
      <Space size={4} align="start">
        <Button
          aria-label="上移"
          icon={<ArrowUpOutlined />}
          style={BTN_STYLE}
          disabled={disabled || index === 0}
          onClick={() => onMove(-1)}
        />
        <Button
          aria-label="下移"
          icon={<ArrowDownOutlined />}
          style={BTN_STYLE}
          disabled={disabled || index === total - 1}
          onClick={() => onMove(1)}
        />
        <Button aria-label="删除" danger icon={<DeleteOutlined />} style={BTN_STYLE} disabled={disabled} onClick={onRemove} />
      </Space>
    </div>
  )
}

interface MpBannerListFieldProps {
  value?: MpBanner[]
  onChange?: (v: MpBanner[]) => void
  disabled?: boolean
}

/** antd Form 自定义字段：value = MpBanner[]；挂在 <Form.Item name="mp_banner" rules={[{ validator: validateMpBanners }]}> 下 */
const MpBannerListField: React.FC<MpBannerListFieldProps> = ({ value, onChange, disabled }) => {
  const list = value ?? []
  const emit = (next: MpBanner[]) => onChange?.(next)

  const patchAt = (i: number, p: Partial<MpBanner>) => emit(list.map((b, j) => (j === i ? { ...b, ...p } : b)))
  const moveAt = (i: number, delta: -1 | 1) => {
    const j = i + delta
    if (j < 0 || j >= list.length) return
    const next = [...list]
    ;[next[i], next[j]] = [next[j], next[i]]
    emit(next)
  }
  const removeAt = (i: number) => emit(list.filter((_b, j) => j !== i))
  const add = () => emit([...list, { imageUrl: '', imageUrlEn: null, bgColor: null }])

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {list.length === 0 && <Text type="secondary" style={{ fontSize: 12 }}>暂未配置横幅，小程序首页不显示横幅。</Text>}
      {list.map((b, i) => (
        <BannerRow
          key={i}
          banner={b}
          index={i}
          total={list.length}
          disabled={disabled}
          onPatch={(p) => patchAt(i, p)}
          onMove={(d) => moveAt(i, d)}
          onRemove={() => removeAt(i)}
        />
      ))}
      {list.length < MP_BANNER_MAX && (
        <Button icon={<PlusOutlined />} disabled={disabled} onClick={add} style={{ minHeight: 44 }}>
          新增横幅（{list.length}/{MP_BANNER_MAX}）
        </Button>
      )}
      <Text type="secondary" style={{ fontSize: 12 }}>
        按列表顺序轮播；中文图必填，英文图选填（留空则英文模式显示中文图）；背景色用于图片两侧留白处，可不设。
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        建议 750×421（约 16:9）JPG，单张不超过 300KB；原图过大手机会显示空白
      </Text>
    </Space>
  )
}

export default MpBannerListField
